import { Injectable } from '@angular/core'
import {
  Observable,
  combineLatest,
  concatMap,
  from,
  last,
  map,
  of,
  scan,
  switchMap,
} from 'rxjs'
import { GoodsReturnsService } from './goods-returns.service'
import { Page } from '../../models/util.model'
import { parseGoodsReturnPageKeys } from './libs/goods-returns-page.lib'
import { Channel, ChannelsService } from '../channels'
import { Payment, PaymentsService } from '../payments'
import { CountriesService, Country } from '../countries'
import { Reason, ReasonsService, ReasonType } from '../reasons'
import { Tag, TagsService } from '../tags'
import { GOODS_RETURN_SYSTEM_TAGS } from './consts/goods-return.const'
import { uniqBy } from 'lodash'
import {
  NgModalServiceInterface,
  ModalResponse,
} from '../../models/modal.model'
import { NgNotificationServiceInterface } from '../../models/notification.model'
import {
  GoodsReturnModalCode,
  GoodsReturnNotificationCode,
  parseGoodsReturnModal,
  parseGoodsReturnNotification,
} from './libs/goods-return-notification.lib'
import {
  GoodsReturnField,
  GoodsReturnSearchParams,
} from './models/goods-return-search.model'
import {
  GoodsReturnsListingData,
  GoodsReturnsListingPage,
} from './models/goods-return-listing.model'
import { GoodsReturn } from './models/goods-return.entity'
import { Order, OrdersService } from '../orders'

@Injectable({
  providedIn: 'root',
})
export class GoodsReturnsRepository {
  constructor(
    private goodsReturnsService: GoodsReturnsService,
    private channelsService: ChannelsService,
    private paymentsService: PaymentsService,
    private countriesService: CountriesService,
    private reasonsService: ReasonsService,
    private ordersService: OrdersService,
    private tagsService: TagsService,
    private ngModalService?: NgModalServiceInterface,
    private ngNotificationService?: NgNotificationServiceInterface,
  ) {}

  /**
   * Search goods-returns and external data
   * @param searchParams - the search params
   * @param fields - the fields
   * @param listingData - the already loaded listing data
   * @returns the observable for search goods-returns
   */
  searchGoodsReturns$(
    searchParams: GoodsReturnSearchParams,
    fields: GoodsReturnField[],
    listingData?: GoodsReturnsListingData,
    returnAll?: boolean,
  ): Observable<GoodsReturnsListingPage> {
    const needsPagination =
      searchParams.limit !== undefined &&
      searchParams.limit > this.goodsReturnsService.limit

    return needsPagination
      ? this._paginateGoodsReturns$(searchParams, fields, listingData)
      : this._searchGoodsReturns$(searchParams, fields, listingData, returnAll)
  }

  /**
   * Search goods returns with relative external data
   * @param searchParams - the search params
   * @param fields - the goods-returns fields
   * @param listingData - the already loaded listing data
   * @returns the observable<GoodsReturnsListingPage> for search goods-returns
   */
  private _searchGoodsReturns$(
    searchParams: GoodsReturnSearchParams,
    fields: GoodsReturnField[],
    listingData?: GoodsReturnsListingData,
    returnAll?: boolean,
  ): Observable<GoodsReturnsListingPage> {
    return this.goodsReturnsService
      .search$(searchParams, returnAll)
      .pipe(
        switchMap((page) =>
          this._loadExtData$(page, fields, listingData).pipe(
            map((extData) => ({ ...page, extData })),
          ),
        ),
      )
  }

  /**
   * Load external page goods-returns data
   * @param page - the goods-returns page
   * @param fields - the goods-returns fields
   * @returns the observable for load external data
   */
  private _loadExtData$(
    page: Page<GoodsReturn>,
    fields: GoodsReturnField[],
    listingData?: GoodsReturnsListingData,
  ): Observable<GoodsReturnsListingData> {
    const extDataKeys = parseGoodsReturnPageKeys(page)
    const obs$: Partial<{
      channels: Observable<Channel[]>
      payments: Observable<Payment[]>
      reasons: Observable<Reason[]>
      countries: Observable<Country[]>
      tags: Observable<Tag[]>
      orders: Observable<Order[]>
    }> = {}

    // Only load channels if we don't already have them or if we need new ones
    if (fields.includes('header.channelId') && extDataKeys.channelIds) {
      const existingChannels = listingData?.channels || []
      const hasAllChannels = extDataKeys.channelIds.every((id) =>
        existingChannels.some((channel) => channel._id === id),
      )

      if (!hasAllChannels) {
        obs$.channels = this.channelsService.store$(
          extDataKeys.channelIds,
          existingChannels,
        )
      } else {
        obs$.channels = of(existingChannels)
      }
    }

    // Only load payments if we don't already have them or if we need new ones
    if (fields.includes('header.paymentId') && extDataKeys.paymentIds) {
      const existingPayments = listingData?.payments || []
      const hasAllPayments = extDataKeys.paymentIds.every((id) =>
        existingPayments.some((payment) => payment._id === id),
      )

      if (!hasAllPayments) {
        obs$.payments = this.paymentsService.store$(
          extDataKeys.paymentIds,
          existingPayments,
        )
      } else {
        obs$.payments = of(existingPayments)
      }
    }

    // Load reasons if header.reasonId field is included, regardless of whether there are results
    if (fields.includes('header.reasonId')) {
      if (!listingData?.reasons || listingData.reasons.length === 0) {
        obs$.reasons = this.reasonsService.list$({
          reason: ReasonType.return,
          visible: true,
        })
      } else {
        obs$.reasons = of(listingData.reasons)
      }
    }

    // Only load countries if we don't already have them or if we need new ones
    if (
      extDataKeys.countryCodes &&
      fields.includes('header.shippingAddress.countryCode')
    ) {
      const existingCountries = listingData?.countries || []
      const hasAllCountries = extDataKeys.countryCodes.every((code) =>
        existingCountries.some((country) => country.alpha2Code === code),
      )

      if (!hasAllCountries) {
        obs$.countries = this.countriesService.store$(
          extDataKeys.countryCodes,
          existingCountries,
        )
      } else {
        obs$.countries = of(existingCountries)
      }
    }

    // Only load tags if we don't already have them or if we need new ones
    if (extDataKeys.tags && fields.includes('tags')) {
      const existingTags = listingData?.tags || []
      const filteredTags = extDataKeys.tags.filter((t) => !t.startsWith('#'))
      const hasAllTags = filteredTags.every((tag) =>
        existingTags.some((existingTag) => existingTag.value === tag),
      )
      const systemTags = GOODS_RETURN_SYSTEM_TAGS.map((tag) => ({
        target: 'goodsReturns',
        value: tag,
        color: '#000000',
      })) as Tag[]

      if (!hasAllTags) {
        obs$.tags = this.tagsService
          .store$(filteredTags, existingTags, 'value', 'goodsReturns')
          .pipe(map((tags) => uniqBy([...tags, ...systemTags], 'value')))
      } else {
        obs$.tags = of(uniqBy([...existingTags, ...systemTags], 'value'))
      }
    }

    if (fields.includes('packages') && extDataKeys.orderIds) {
      const existingOrders = listingData?.orders || []
      const hasAllOrders = extDataKeys.orderIds.every((id) =>
        existingOrders.some((order) => order._id === id),
      )

      if (!hasAllOrders && extDataKeys.orderIds.length > 0) {
        obs$.orders = this.ordersService.list$({ _id: extDataKeys.orderIds })
      } else {
        obs$.orders = of(existingOrders)
      }
    }

    if (!Object.keys(obs$).length) {
      return of({} as GoodsReturnsListingData)
    }

    return combineLatest(obs$).pipe(
      map((results) => {
        const extData: GoodsReturnsListingData = {}
        if (results.channels) extData.channels = results.channels
        if (results.payments) extData.payments = results.payments
        if (results.reasons) extData.reasons = results.reasons
        if (results.countries) extData.countries = results.countries
        if (results.tags) extData.tags = results.tags
        if (results.orders) extData.orders = results.orders
        return extData
      }),
    )
  }

  /**
   * Search more than 200 goods-returns with relative external data
   * @param searchParams - the search params
   * @param fields - the goods-returns fields
   * @param listingData - the already loaded listing data
   * @returns the observable<GoodsReturnsListingPage> for search goods-returns
   */
  private _paginateGoodsReturns$(
    searchParams: GoodsReturnSearchParams,
    fields: GoodsReturnField[],
    listingData?: GoodsReturnsListingData,
  ): Observable<GoodsReturnsListingPage> {
    const totalCount = searchParams.limit || this.goodsReturnsService.limit
    const pagesCount = Math.ceil(totalCount / this.goodsReturnsService.limit)

    return from(
      Array.from({ length: pagesCount }, (_, i) => ({
        ...searchParams,
        limit: this.goodsReturnsService.limit,
        offset: i * this.goodsReturnsService.limit,
      })),
    ).pipe(
      concatMap((pageParams) =>
        this._searchGoodsReturns$(pageParams, fields, listingData),
      ),
      scan<GoodsReturnsListingPage, GoodsReturnsListingPage>(
        (acc, current) => ({
          totalCount: uniqBy(
            [...(acc.data || []), ...(current.data || [])],
            '_id',
          ).length,
          data: uniqBy([...(acc.data || []), ...(current.data || [])], '_id'),
          extData: { ...acc.extData, ...current.extData },
        }),
        { totalCount: 0, data: [], extData: {} } as GoodsReturnsListingPage,
      ),
      last(),
    )
  }

  notify(options: {
    code?: GoodsReturnNotificationCode
    message?: string
  }): void {
    this.ngNotificationService?.show(
      options.message
        ? { title: 'Error', message: options.message, color: 'error' }
        : parseGoodsReturnNotification(options.code),
    )
  }

  async alert(
    code: GoodsReturnModalCode,
    goodsReturn?: GoodsReturn,
  ): Promise<ModalResponse> {
    const settings = parseGoodsReturnModal(code, goodsReturn)
    if (!this.ngModalService || !settings) {
      return 'closed'
    }
    return await this.ngModalService.showDialog(settings)
  }
}
